import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { type CalendarPeriod } from 'common/interfaces/ICartContent'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import type {
  IOptionShopCartItem,
  IOptionShopCartOption,
  OptionShopDays,
} from 'features/Option/interfaces/IOptionShopCartItem'
import { serializeShopCartForApi } from 'features/Option/services/serializeShopCartForApi'

export const optionApi = createApi({
  reducerPath: REDUCERS.OPTION,
  baseQuery: baseQuery(),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    optionInfo: build.query({
      query: ({ id, siteId }) => ({
        url: `/cart/option/${id}`,
        headers: { 'Database-Connection': siteId },
      }),
    }),
    shopOptionInfo: build.query<
      IOptionShopCartOption,
      {
        bookingId: string
        siteId: string
        optionId: number
        optionType: string
        selectedDate?: string
      }
    >({
      query: ({ bookingId, siteId, optionId, optionType, selectedDate }) => ({
        url: `leases/${bookingId}/shop/${optionType}/${optionId}${
          selectedDate ? `/${selectedDate}` : ''
        }`,
        headers: { 'Database-Connection': siteId },
      }),
    }),
    shopOptionDays: build.query<
      OptionShopDays,
      {
        bookingId: string
        siteId: string
        optionId: number
        optionType: string
        date: CalendarPeriod
      }
    >({
      query: ({ bookingId, siteId, optionId, optionType, date }) => ({
        url: `leases/${bookingId}/shop/${optionType}/${optionId}/shop-days/${date}`,
        headers: { 'Database-Connection': siteId },
      }),
    }),
    createShopCart: build.mutation({
      query: (data: IOptionShopCartItem[]) => ({
        data: serializeShopCartForApi(data),
        url: `/leases/shop-cart`,
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: { data: IOptionShopCartItem }[]): IOptionShopCartItem[] =>
        response.map(({ data }) => data),
    }),
    fetchShopCart: build.query({
      query: () => ({
        url: `/leases-shop-cart`,
      }),
      transformResponse: (response: { data: IOptionShopCartItem }[]): IOptionShopCartItem[] =>
        response.map(({ data }) => data),
    }),
    updateShopCart: build.mutation({
      query: (data: IOptionShopCartItem[]) => ({
        data: serializeShopCartForApi(data),
        url: `/leases/shop-cart`,
        method: HTTP_METHODS.PUT,
      }),
      transformResponse: (response: { data: IOptionShopCartItem }[]): IOptionShopCartItem[] =>
        response.map(({ data }) => data),
    }),
    checkoutLeaseShop: build.mutation({
      query: ({ bookingId, paymentMethodId, billing_details, siteId }) => ({
        data: { paymentMethodId, billing_details },
        url: `/leases/${bookingId}/checkout`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
      }),
      invalidatesTags: ['Levels'],
    }),
    deleteShopCart: build.mutation({
      query: () => ({
        url: `/leases/shop-cart`,
        method: HTTP_METHODS.DELETE,
      }),
    }),
  }),
})

export const {
  useOptionInfoQuery,
  useLazyFetchShopCartQuery,
  useCreateShopCartMutation,
  useUpdateShopCartMutation,
  useDeleteShopCartMutation,
  useCheckoutLeaseShopMutation,
  useLazyOptionInfoQuery,
}: any = optionApi

export const { useShopOptionInfoQuery, useLazyShopOptionInfoQuery, useShopOptionDaysQuery } =
  optionApi
